import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Skeleton from '@components/Atoms/Skeleton';
import Table from '@components/Atoms/Table';
import { PastReportsContainer } from './ReportGenerator.style';
import { BlockTitle, BlockWrap } from '../Generals/stylesheets/General.style';
import { usePastReportsQuery } from './ReportQueries';
import useWebSocketListener from './hooks/useWebSocketListener';


const PastReportsList: React.FC = () => {
    useWebSocketListener();
    const { isFetching, data: pastReportsList } = usePastReportsQuery();

    const getStatusLabel = (status: string): string => {
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const transformedRows = useMemo(() => {
        if (!pastReportsList?.results) return [];
        return pastReportsList.results.map((report) => ({
            id: report.id,
            reportName: report,
            reportType: report.report_type,
            lastGenerated: report.last_generated,
            status: report.status,
        }));
    }, [pastReportsList?.results]);

    const columns = [
        {
            id: 1,
            headerTitle: 'Report Name',
            relatedKey: 'reportName',
            defaultValue: '',
            isSortable: false,
            width: '400px',
            valueGetter: (report: any) => {
                const extension = report.report_extension?.toLowerCase();
                const isDirectDownload = extension === 'xlsx' || extension === 'zip';
                if (isDirectDownload) {
                    return (
                        <a href={report.report_url} target="_blank" rel="noopener noreferrer">
                            <span>{report.report_name}</span>
                        </a>
                    );
                }
                return (
                    <Link
                        to={{
                            pathname: `/admin/reports/generator/${report.id}`,
                            state: { pdf: report.report_url, from: '/admin/reports/generator' },
                        }}
                    >
                        <span>{report.report_name}</span>
                    </Link>
                );
            },
        },
        {
            id: 2,
            headerTitle: 'Report Type',
            relatedKey: 'reportType',
            defaultValue: '',
            isSortable: true,
        },
        {
            id: 3,
            headerTitle: 'Last Generated',
            relatedKey: 'lastGenerated',
            defaultValue: '',
            isSortable: true,
            valueGetter: (dateString: string) => formatDate(dateString),
        },
        {
            id: 4,
            headerTitle: 'Status',
            relatedKey: 'status',
            defaultValue: '',
            isSortable: true,
            valueGetter: (status: string) => getStatusLabel(status),
        },
    ];
    return (
        <PastReportsContainer data-testid="pastReportsWrapper">
            <BlockWrap>
                <BlockTitle>
                    <span>Past Reports</span>
                </BlockTitle>
                {isFetching ? (
                    <Skeleton variant="rectangular" width="100%" height={200} />
                ) : (
                    <>
                    <Table defaultRowsPerPage={5} rowsPerPageOptions={[5, 10, 20, 50, 100]} rows={transformedRows} columns={columns} hasPagination />
                    </>
                )}
            </BlockWrap>
        </PastReportsContainer>
    );
};

export default PastReportsList;

